import { useState } from 'react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { Switch } from '@rocco/components/switch';
import { Label } from '@rocco/components/label';
import { Pencil } from 'lucide-react';
import type { BuildHubFieldSelection } from '../../hooks/use-build-hub-field-selection';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { cn } from '@rocco/utils/cn';

interface BuildHubFieldCustomizerProps {
    /** The BuildHub project whose fields are being customised */
    buildHub: BuildHubOption;
    /** Current field selection state */
    fieldSelection: BuildHubFieldSelection;
    /** Callback when field selection changes */
    onFieldSelectionChange: (selection: BuildHubFieldSelection) => void;
    /** Additional className */
    className?: string;
}

/**
 * Popover component for customizing which BuildHub fields to share in enquiry.
 * Only shows toggles for fields that the selected project actually has values for.
 * Triggered by a pencil icon button.
 */
export function BuildHubFieldCustomizer({
    buildHub,
    fieldSelection,
    onFieldSelectionChange,
    className,
}: BuildHubFieldCustomizerProps) {
    const [isOpen, setIsOpen] = useState(false);

    const handleFieldChange = (
        field: keyof BuildHubFieldSelection,
        checked: boolean,
    ) => {
        onFieldSelectionChange({
            ...fieldSelection,
            [field]: checked,
        });
    };

    const hasLocation = !!(buildHub.Location || buildHub.LocationData);
    const hasBudget = !!(buildHub.Budget?.Min || buildHub.Budget?.Max);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700',
                        className,
                    )}
                    aria-label="Customise fields to share"
                >
                    <Pencil className="h-4 w-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4" align="end" sideOffset={8}>
                <div className="space-y-4">
                    <div>
                        <h3 className="text-base font-medium text-gray-900">
                            {`Customise fields to share`}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {`Choose which project details to include in your enquiry`}
                        </p>
                    </div>

                    <div className="space-y-2">
                        {/* Title - locked */}
                        <div className="flex items-center justify-between">
                            <Label
                                htmlFor="field-title"
                                className="text-sm text-gray-700"
                            >
                                Title
                            </Label>
                            <div className="flex items-center gap-2">
                                <Switch
                                    id="field-title"
                                    checked={true}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        {buildHub.ProjectScope && (
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="field-scope"
                                    className="text-sm text-gray-700"
                                >
                                    {`Project Scope`}
                                </Label>
                                <Switch
                                    id="field-scope"
                                    checked={fieldSelection.includeScope}
                                    onCheckedChange={(checked) =>
                                        handleFieldChange(
                                            'includeScope',
                                            !!checked,
                                        )
                                    }
                                />
                            </div>
                        )}

                        {buildHub.BuildStage && (
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="field-stage"
                                    className="text-sm text-gray-700"
                                >
                                    {`Project Stage`}
                                </Label>
                                <Switch
                                    id="field-stage"
                                    checked={fieldSelection.includeStage}
                                    onCheckedChange={(checked) =>
                                        handleFieldChange(
                                            'includeStage',
                                            !!checked,
                                        )
                                    }
                                />
                            </div>
                        )}

                        {hasBudget && (
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="field-budget"
                                    className="text-sm text-gray-700"
                                >
                                    Total Project Budget
                                </Label>
                                <Switch
                                    id="field-budget"
                                    checked={fieldSelection.includeBudget}
                                    onCheckedChange={(checked) =>
                                        handleFieldChange(
                                            'includeBudget',
                                            !!checked,
                                        )
                                    }
                                />
                            </div>
                        )}

                        {hasLocation && (
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="field-full-address"
                                    className="text-sm text-gray-700"
                                >
                                    Show full address
                                </Label>
                                <Switch
                                    id="field-full-address"
                                    checked={fieldSelection.includeFullAddress}
                                    onCheckedChange={(checked) =>
                                        handleFieldChange(
                                            'includeFullAddress',
                                            !!checked,
                                        )
                                    }
                                />
                            </div>
                        )}

                        {buildHub.StartDate && (
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="field-start-date"
                                    className="text-sm text-gray-700"
                                >
                                    Start Date
                                </Label>
                                <Switch
                                    id="field-start-date"
                                    checked={fieldSelection.includeStartDate}
                                    onCheckedChange={(checked) =>
                                        handleFieldChange(
                                            'includeStartDate',
                                            !!checked,
                                        )
                                    }
                                />
                            </div>
                        )}

                        {buildHub.EndDate && (
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor="field-end-date"
                                    className="text-sm text-gray-700"
                                >
                                    End Date
                                </Label>
                                <Switch
                                    id="field-end-date"
                                    checked={fieldSelection.includeEndDate}
                                    onCheckedChange={(checked) =>
                                        handleFieldChange(
                                            'includeEndDate',
                                            !!checked,
                                        )
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
